/**
 * ProviderWeeklyScheduleModal
 * 3-step modal for configuring per-weekday schedules for clinics using "Hora Marcada".
 *
 * Step 1: Select active days
 * Step 2: Set default times + slot configuration for all selected days
 * Step 3: Adjust individual days if needed
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stepper,
  Step,
  StepLabel,
  Box,
  Typography,
  Grid,
  TextField,
  CircularProgress,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Divider,
} from '@mui/material';
import {
  NavigateNext as NextIcon,
  NavigateBefore as BackIcon,
  Save as SaveIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import WeekDaySelector from '../../professional-schedules/components/WeekDaySelector';
import { WeekDay, WEEK_DAY_NAMES } from '../../../types/professionalSchedule';
import { ProviderWeeklySchedule } from '../../../types/provider';
import { providerService } from '../../../services/providerService';

interface DayConfig {
  weekDay: number;
  morningStart: string;
  morningEnd: string;
  afternoonStart: string;
  afternoonEnd: string;
  capacity: string;
}

export interface SlotConfig {
  defaultSlotDuration?: number;
  concurrentCapacity?: number;
}

interface ProviderWeeklyScheduleModalProps {
  open: boolean;
  onClose: (slotConfig?: SlotConfig) => void;
  providerId: string | number;
  providerName?: string;
  defaultMorningStart?: string;
  defaultMorningEnd?: string;
  defaultAfternoonStart?: string;
  defaultAfternoonEnd?: string;
  initialSlotDuration?: number;
  initialConcurrentCapacity?: number;
}

const STEPS = ['Dias Ativos', 'Horário Padrão', 'Ajuste por Dia'];

const createEmptyDayConfig = (weekDay: number): DayConfig => ({
  weekDay,
  morningStart: '',
  morningEnd: '',
  afternoonStart: '',
  afternoonEnd: '',
  capacity: '',
});

const ProviderWeeklyScheduleModal: React.FC<ProviderWeeklyScheduleModalProps> = ({
  open,
  onClose,
  providerId,
  providerName,
  defaultMorningStart = '',
  defaultMorningEnd = '',
  defaultAfternoonStart = '',
  defaultAfternoonEnd = '',
  initialSlotDuration,
  initialConcurrentCapacity,
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [selectedDays, setSelectedDays] = useState<WeekDay[]>([]);
  const [defaultTimes, setDefaultTimes] = useState({
    morningStart: defaultMorningStart,
    morningEnd: defaultMorningEnd,
    afternoonStart: defaultAfternoonStart,
    afternoonEnd: defaultAfternoonEnd,
    capacity: '',
  });
  const [slotDuration, setSlotDuration] = useState<number>(initialSlotDuration ?? 30);
  const [concurrentCapacity, setConcurrentCapacity] = useState<string>(
    initialConcurrentCapacity != null ? String(initialConcurrentCapacity) : ''
  );
  const [dayConfigs, setDayConfigs] = useState<DayConfig[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load existing schedule when modal opens
  useEffect(() => {
    if (!open || !providerId) return;

    const loadSchedule = async () => {
      setLoading(true);
      setError(null);
      try {
        const schedules = await providerService.getWeeklySchedule(providerId);
        if (schedules && schedules.length > 0) {
          // Extract unique days
          const days = [...new Set(schedules.map(s => s.weekDay))].sort() as WeekDay[];
          setSelectedDays(days);

          // Build day configs from existing data
          const configs: DayConfig[] = days.map(day => {
            const blocks = schedules.filter(s => s.weekDay === day).sort((a, b) => a.startTime.localeCompare(b.startTime));
            const morning = blocks[0];
            const afternoon = blocks.length > 1 ? blocks[1] : undefined;
            return {
              weekDay: day,
              morningStart: morning?.startTime || '',
              morningEnd: morning?.endTime || '',
              afternoonStart: afternoon?.startTime || '',
              afternoonEnd: afternoon?.endTime || '',
              capacity: morning?.capacity != null ? String(morning.capacity) : '',
            };
          });
          setDayConfigs(configs);

          // Infer default times from first day
          if (configs.length > 0) {
            setDefaultTimes({
              morningStart: configs[0]!.morningStart,
              morningEnd: configs[0]!.morningEnd,
              afternoonStart: configs[0]!.afternoonStart,
              afternoonEnd: configs[0]!.afternoonEnd,
              capacity: configs[0]!.capacity,
            });
          }
        } else {
          // No existing schedule, use defaults from props
          setSelectedDays([]);
          setDayConfigs([]);
          setDefaultTimes({
            morningStart: defaultMorningStart,
            morningEnd: defaultMorningEnd,
            afternoonStart: defaultAfternoonStart,
            afternoonEnd: defaultAfternoonEnd,
            capacity: '',
          });
        }
        // Reset slot config from props
        setSlotDuration(initialSlotDuration ?? 30);
        setConcurrentCapacity(
          initialConcurrentCapacity != null ? String(initialConcurrentCapacity) : ''
        );
      } catch {
        setError('Erro ao carregar horários existentes');
      } finally {
        setLoading(false);
      }
    };

    loadSchedule();
  }, [open, providerId, defaultMorningStart, defaultMorningEnd, defaultAfternoonStart, defaultAfternoonEnd, initialSlotDuration, initialConcurrentCapacity]);

  // Reset when modal closes
  useEffect(() => {
    if (!open) {
      setActiveStep(0);
      setError(null);
    }
  }, [open]);

  const handleNext = useCallback(() => {
    if (activeStep === 0) {
      // Moving from step 1 to 2: sync day configs with selected days
      setDayConfigs(prev => {
        const existing = new Map(prev.map(d => [d.weekDay, d]));
        return selectedDays.map(day =>
          existing.get(day) || createEmptyDayConfig(day)
        );
      });
    }
    if (activeStep === 1) {
      // Moving from step 2 to 3: apply defaults to days that don't have custom times
      setDayConfigs(prev =>
        prev.map(config => ({
          ...config,
          morningStart: config.morningStart || defaultTimes.morningStart,
          morningEnd: config.morningEnd || defaultTimes.morningEnd,
          afternoonStart: config.afternoonStart || defaultTimes.afternoonStart,
          afternoonEnd: config.afternoonEnd || defaultTimes.afternoonEnd,
          capacity: config.capacity || defaultTimes.capacity,
        }))
      );
    }
    setActiveStep(prev => prev + 1);
  }, [activeStep, selectedDays, defaultTimes]);

  const handleBack = () => setActiveStep(prev => prev - 1);

  const handleDayConfigChange = (weekDay: number, field: keyof DayConfig, value: string) => {
    setDayConfigs(prev =>
      prev.map(d => (d.weekDay === weekDay ? { ...d, [field]: value } : d))
    );
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      // Convert dayConfigs to API format (each period becomes a separate entry)
      const entries: Omit<ProviderWeeklySchedule, 'id' | 'providerId' | 'isActive'>[] = [];
      for (const config of dayConfigs) {
        if (config.morningStart && config.morningEnd) {
          entries.push({
            weekDay: config.weekDay,
            startTime: config.morningStart,
            endTime: config.morningEnd,
            capacity: config.capacity ? Number(config.capacity) : null,
          });
        }
        if (config.afternoonStart && config.afternoonEnd) {
          entries.push({
            weekDay: config.weekDay,
            startTime: config.afternoonStart,
            endTime: config.afternoonEnd,
            capacity: config.capacity ? Number(config.capacity) : null,
          });
        }
      }

      await providerService.saveWeeklySchedule(providerId, entries);

      // Pass slot config back to parent form so it stays in sync
      onClose({
        defaultSlotDuration: slotDuration,
        concurrentCapacity: concurrentCapacity ? Number(concurrentCapacity) : undefined,
      });
    } catch {
      setError('Erro ao salvar horários');
    } finally {
      setSaving(false);
    }
  };

  const canAdvance = () => {
    if (activeStep === 0) return selectedDays.length > 0;
    if (activeStep === 1) return defaultTimes.morningStart || defaultTimes.afternoonStart;
    return true;
  };

  return (
    <Dialog open={open} onClose={() => onClose()} maxWidth="md" fullWidth>
      <DialogTitle>
        Horários por Dia da Semana
        {providerName && (
          <Typography variant="body2" color="text.secondary">
            {providerName}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Stepper activeStep={activeStep} sx={{ mb: 3, mt: 1 }}>
              {STEPS.map(label => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {/* Step 1: Select days */}
            {activeStep === 0 && (
              <Box sx={{ py: 2 }}>
                <Typography variant="body1" gutterBottom>
                  Selecione os dias em que a clínica atende:
                </Typography>
                <WeekDaySelector
                  selectedDays={selectedDays}
                  onChange={setSelectedDays}
                />
              </Box>
            )}

            {/* Step 2: Default times + slot config */}
            {activeStep === 1 && (
              <Box sx={{ py: 2 }}>
                <Typography variant="body1" gutterBottom>
                  Defina os horários padrão para todos os dias selecionados:
                </Typography>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  {/* Morning & Afternoon side by side */}
                  <Grid size={6}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Manhã
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <TextField
                        label="Início"
                        value={defaultTimes.morningStart}
                        onChange={e => setDefaultTimes(prev => ({ ...prev, morningStart: e.target.value }))}
                        fullWidth
                        placeholder="07:00"
                        size="small"
                      />
                      <TextField
                        label="Fim"
                        value={defaultTimes.morningEnd}
                        onChange={e => setDefaultTimes(prev => ({ ...prev, morningEnd: e.target.value }))}
                        fullWidth
                        placeholder="12:00"
                        size="small"
                      />
                    </Box>
                  </Grid>
                  <Grid size={6}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Tarde
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <TextField
                        label="Início"
                        value={defaultTimes.afternoonStart}
                        onChange={e => setDefaultTimes(prev => ({ ...prev, afternoonStart: e.target.value }))}
                        fullWidth
                        placeholder="13:00"
                        size="small"
                      />
                      <TextField
                        label="Fim"
                        value={defaultTimes.afternoonEnd}
                        onChange={e => setDefaultTimes(prev => ({ ...prev, afternoonEnd: e.target.value }))}
                        fullWidth
                        placeholder="18:00"
                        size="small"
                      />
                    </Box>
                  </Grid>

                  {/* Divider */}
                  <Grid size={12}>
                    <Divider sx={{ my: 1 }} />
                  </Grid>

                  {/* Slot configuration */}
                  <Grid size={12}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Configuração de Atendimento
                    </Typography>
                  </Grid>
                  <Grid size={4}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Duração</InputLabel>
                      <Select
                        value={slotDuration}
                        label="Duração"
                        onChange={e => setSlotDuration(Number(e.target.value))}
                      >
                        <MenuItem value={15}>15 min</MenuItem>
                        <MenuItem value={20}>20 min</MenuItem>
                        <MenuItem value={25}>25 min</MenuItem>
                        <MenuItem value={30}>30 min</MenuItem>
                        <MenuItem value={40}>40 min</MenuItem>
                        <MenuItem value={45}>45 min</MenuItem>
                        <MenuItem value={60}>60 min</MenuItem>
                        <MenuItem value={90}>90 min</MenuItem>
                        <MenuItem value={120}>120 min</MenuItem>
                      </Select>
                      <FormHelperText>Intervalo entre slots</FormHelperText>
                    </FormControl>
                  </Grid>
                  <Grid size={4}>
                    <TextField
                      label="Simultâneos"
                      value={concurrentCapacity}
                      onChange={e => setConcurrentCapacity(e.target.value)}
                      fullWidth
                      type="number"
                      size="small"
                      placeholder="3"
                      helperText="Atend. ao mesmo tempo"
                    />
                  </Grid>
                  <Grid size={4}>
                    <TextField
                      label="Capacidade/dia"
                      value={defaultTimes.capacity}
                      onChange={e => setDefaultTimes(prev => ({ ...prev, capacity: e.target.value }))}
                      fullWidth
                      type="number"
                      size="small"
                      placeholder="Padrão"
                      helperText="Override por bloco"
                    />
                  </Grid>
                </Grid>
              </Box>
            )}

            {/* Step 3: Per-day adjustment */}
            {activeStep === 2 && (
              <Box sx={{ py: 2 }}>
                <Typography variant="body1" gutterBottom>
                  Ajuste individualmente se necessário:
                </Typography>
                {dayConfigs.map(config => (
                  <Accordion key={config.weekDay} defaultExpanded={dayConfigs.length <= 3}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography fontWeight="bold">
                        {WEEK_DAY_NAMES[config.weekDay as WeekDay]}
                      </Typography>
                      {config.morningStart && (
                        <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                          {config.morningStart}-{config.morningEnd}
                          {config.afternoonStart && ` / ${config.afternoonStart}-${config.afternoonEnd}`}
                        </Typography>
                      )}
                    </AccordionSummary>
                    <AccordionDetails>
                      <Grid container spacing={2} alignItems="flex-end">
                        <Grid size={5}>
                          <Typography variant="caption" color="text.secondary">Manhã</Typography>
                          <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                            <TextField
                              label="Início"
                              value={config.morningStart}
                              onChange={e => handleDayConfigChange(config.weekDay, 'morningStart', e.target.value)}
                              size="small"
                              placeholder="07:00"
                            />
                            <TextField
                              label="Fim"
                              value={config.morningEnd}
                              onChange={e => handleDayConfigChange(config.weekDay, 'morningEnd', e.target.value)}
                              size="small"
                              placeholder="12:00"
                            />
                          </Box>
                        </Grid>
                        <Grid size={5}>
                          <Typography variant="caption" color="text.secondary">Tarde</Typography>
                          <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                            <TextField
                              label="Início"
                              value={config.afternoonStart}
                              onChange={e => handleDayConfigChange(config.weekDay, 'afternoonStart', e.target.value)}
                              size="small"
                              placeholder="13:00"
                            />
                            <TextField
                              label="Fim"
                              value={config.afternoonEnd}
                              onChange={e => handleDayConfigChange(config.weekDay, 'afternoonEnd', e.target.value)}
                              size="small"
                              placeholder="18:00"
                            />
                          </Box>
                        </Grid>
                        <Grid size={2}>
                          <TextField
                            label="Cap."
                            value={config.capacity}
                            onChange={e => handleDayConfigChange(config.weekDay, 'capacity', e.target.value)}
                            fullWidth
                            type="number"
                            size="small"
                            placeholder="—"
                          />
                        </Grid>
                      </Grid>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Box>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose()} disabled={saving}>
          Cancelar
        </Button>
        {activeStep > 0 && (
          <Button onClick={handleBack} startIcon={<BackIcon />} disabled={saving}>
            Voltar
          </Button>
        )}
        {activeStep < STEPS.length - 1 ? (
          <Button
            onClick={handleNext}
            endIcon={<NextIcon />}
            variant="contained"
            disabled={!canAdvance()}
          >
            Avançar
          </Button>
        ) : (
          <Button
            onClick={handleSave}
            startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
            variant="contained"
            disabled={saving}
          >
            Salvar
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ProviderWeeklyScheduleModal;
